// pendingFileStore — IndexedDB-backed persistence for the wizard's
// queued (pre-create) file uploads.
//
// Why this exists. Both project-creation flows (SmartIntakePage and
// NewProjectWizardPage) collect supporting/format documents as in-memory
// `File` objects in Step 1 — BEFORE the project exists — then upload them
// after `createProject` succeeds on submit. The *text* portion of the
// wizard is persisted to localStorage via the draft stores, but `File`
// objects can't be JSON-serialised, so the draft stores deliberately skip
// them. The consequence was a silent data-loss bug: a reload, a
// "Save & Exit" → "Resume", or any navigation away dropped the queued
// files while the typed problem statement survived — so the document
// never reached Phase 2 (Observer). (See the AI Customer Support Agent
// project, whose PDF source vanished exactly this way.)
//
// IndexedDB *can* store Blobs/Files via structured clone, so we mirror the
// pending-file queue here keyed by the same draft "slot" the text draft
// uses. The store degrades gracefully to a no-op when IndexedDB is
// unavailable (private-mode quirks, SSR, blocked storage) — callers keep
// their existing in-memory behaviour and simply lose the persistence win.

import type { PendingFile } from '@/components/intake/InputUploader'

const DB_NAME = 'ai_alm_pending_files'
const DB_VERSION = 1
const STORE = 'slots'

/** One slot per wizard flow so the two never clobber each other. */
export type PendingSlot = 'intake' | 'new_project'

/**
 * We persist the file's bytes plus the metadata `uploadInput` /
 * `InputUploader` rely on, then rebuild a real `File` on load. Storing the
 * raw `Blob` + metadata (rather than the `File` directly) keeps the
 * round-trip robust across browsers that don't preserve the `File`
 * subtype through structured clone.
 */
interface StoredFile {
  id: string
  name: string
  type: string
  lastModified: number
  blob: Blob
}

interface SlotRecord {
  slot: PendingSlot
  content: StoredFile[]
  format: StoredFile[]
  savedAt: string
}

export interface PendingFileSet {
  contentFiles: PendingFile[]
  formatFiles: PendingFile[]
}

const EMPTY: PendingFileSet = { contentFiles: [], formatFiles: [] }

function idbAvailable(): boolean {
  return typeof indexedDB !== 'undefined'
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'slot' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function toStored(files: PendingFile[]): StoredFile[] {
  return files.map((pf) => ({
    id: pf.id,
    name: pf.file.name,
    type: pf.file.type,
    lastModified: pf.file.lastModified,
    blob: pf.file,
  }))
}

function fromStored(stored: StoredFile[]): PendingFile[] {
  return stored.map((s) => ({
    id: s.id,
    file: new File([s.blob], s.name, {
      type: s.type,
      lastModified: s.lastModified,
    }),
  }))
}

/**
 * Mirror the current pending-file queue for `slot` into IndexedDB. An
 * empty queue clears the slot so deleted files are never resurrected on
 * the next resume. Best-effort: any failure is swallowed so persistence
 * can never block the wizard.
 */
export async function savePendingFiles(
  slot: PendingSlot,
  files: PendingFileSet,
): Promise<void> {
  if (!idbAvailable()) return
  if (files.contentFiles.length === 0 && files.formatFiles.length === 0) {
    await clearPendingFiles(slot)
    return
  }
  try {
    const db = await openDb()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite')
      const record: SlotRecord = {
        slot,
        content: toStored(files.contentFiles),
        format: toStored(files.formatFiles),
        savedAt: new Date().toISOString(),
      }
      tx.objectStore(STORE).put(record)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
    db.close()
  } catch {
    // Best-effort — a persistence failure must never break the flow.
  }
}

/** Load the queued files for `slot`, rebuilt as fresh `File` objects. */
export async function loadPendingFiles(slot: PendingSlot): Promise<PendingFileSet> {
  if (!idbAvailable()) return EMPTY
  try {
    const db = await openDb()
    const record = await new Promise<SlotRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly')
      const req = tx.objectStore(STORE).get(slot)
      req.onsuccess = () => resolve(req.result as SlotRecord | undefined)
      req.onerror = () => reject(req.error)
    })
    db.close()
    if (!record) return EMPTY
    return {
      contentFiles: fromStored(record.content ?? []),
      formatFiles: fromStored(record.format ?? []),
    }
  } catch {
    return EMPTY
  }
}

/** Drop the queued files for `slot` (post-submit / discard). */
export async function clearPendingFiles(slot: PendingSlot): Promise<void> {
  if (!idbAvailable()) return
  try {
    const db = await openDb()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).delete(slot)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
    db.close()
  } catch {
    // Best-effort.
  }
}
